import { X } from "lucide-react";
import { useEffect, useRef, type ReactNode } from "react";

/**
 * Modal réutilisable — overlay backdrop + dialog centré.
 *
 * A11y :
 *  - `role="dialog"` + `aria-modal="true"`
 *  - Focus trap natif (Tab / Shift+Tab bouclent sur les éléments focusables)
 *  - Restaure le focus sur l'élément qui avait le focus avant l'ouverture
 *  - Escape ferme la modal (désactivable via `closeOnEscape={false}`)
 */
export interface ModalProps {
  open: boolean;
  onClose: () => void;
  /** Titre affiché en haut. Si fourni, devient le label aria de la dialog. */
  title?: string;
  /** Largeur max : `sm` (max-w-sm) / `md` (max-w-lg) / `lg` (max-w-2xl). */
  size?: "sm" | "md" | "lg";
  /** Cliquer le backdrop ferme la modal (défaut true). */
  closeOnBackdrop?: boolean;
  /**
   * La touche Échap ferme la modal (défaut true). À passer `false` sur les
   * modales de SAISIE (comme `closeOnBackdrop={false}`) pour ne pas perdre le
   * formulaire sur un appui accidentel (terrain, mains mouillées). Le focus trap
   * Tab / Shift+Tab reste actif quelle que soit la valeur.
   */
  closeOnEscape?: boolean;
  /**
   * Le voile laisse passer les clics vers la page (défaut false).
   *
   * Pour les modales d'INSPECTION posées sur une carte ou un plan : sans ça, le
   * voile avale le clic suivant, si bien que passer d'un élément à l'autre
   * demande deux clics — un pour fermer, un pour rouvrir — sans que rien ne
   * l'explique à l'écran. Le voile ne ferme alors plus au clic (il n'en reçoit
   * plus) : la croix et Échap restent les sorties, et `aria-modal` passe à
   * false puisque le reste de la page demeure utilisable.
   */
  backdropClickThrough?: boolean;
  children: ReactNode;
}

const sizeClasses: Record<NonNullable<ModalProps["size"]>, string> = {
  sm: "max-w-sm",
  md: "max-w-lg",
  lg: "max-w-2xl",
};

// Sélecteur des éléments focusables — utilisé pour le focus trap. Couvre les
// cas usuels sans dépendre d'une lib externe (tabbable / focus-trap-react).
const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

export function Modal({
  open,
  onClose,
  title,
  size = "md",
  closeOnBackdrop = true,
  closeOnEscape = true,
  backdropClickThrough = false,
  children,
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  // Ref vers l'élément qui détient le focus avant ouverture → restauré au close.
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  // Callbacks/options lus via des refs (« latest ref ») : l'effet de gestion du
  // focus ne dépend QUE de `open`. Sans ça, un `onClose` inline — recréé à
  // chaque frappe par un parent qui porte l'état de saisie — re-déclenchait
  // l'effet à chaque re-render : `dialogRef.current?.focus()` volait alors le
  // focus de l'input à chaque caractère (bug « seul le 1er caractère passe »).
  const onCloseRef = useRef(onClose);
  const closeOnEscapeRef = useRef(closeOnEscape);
  useEffect(() => {
    onCloseRef.current = onClose;
    closeOnEscapeRef.current = closeOnEscape;
  });

  // Escape ferme + restauration du focus à la fermeture.
  useEffect(() => {
    if (!open) return;
    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        // Sur une modale de saisie (`closeOnEscape={false}`), Échap ne ferme pas
        // (on ne perd pas le formulaire) mais le focus trap Tab reste actif.
        if (!closeOnEscapeRef.current) return;
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      // Focus trap : on cycle Tab / Shift+Tab dans la modal uniquement.
      if (event.key === "Tab" && dialogRef.current) {
        const focusables = dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
        if (focusables.length === 0) {
          event.preventDefault();
          return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (event.shiftKey && active === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && active === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    }

    document.addEventListener("keydown", onKeyDown);
    // Focus initial sur le conteneur dialog lui-même (tabIndex=-1) : la modal
    // s'ouvre TOUJOURS en haut (un article long n'est pas scrollé sur un lien),
    // le clavier mobile ne surgit pas sur une modale de saisie, et Tab entre
    // ensuite normalement dans le contenu (focus trap actif).
    dialogRef.current?.focus();

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      previouslyFocusedRef.current?.focus();
    };
    // `open` UNIQUEMENT : le focus initial + la restauration ne doivent jouer
    // qu'à l'ouverture/fermeture, jamais sur un simple re-render du parent.
  }, [open]);

  if (!open) return null;

  return (
    <div
      className={[
        "fixed inset-0 z-[80] flex items-center justify-center p-4",
        // Voile traversant : pas d'assombrissement (il masquerait un plan qu'on
        // veut continuer à lire) et aucun clic capté.
        backdropClickThrough ? "pointer-events-none bg-transparent" : "bg-black/40",
      ].join(" ")}
      onClick={closeOnBackdrop && !backdropClickThrough ? onClose : undefined}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal={!backdropClickThrough}
        aria-label={title}
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
        className={[
          backdropClickThrough ? "pointer-events-auto" : "",
          // `flex-col` + hauteur bornée à la fenêtre (moins le p-4 de l'overlay) :
          // l'en-tête reste fixe et SEUL le corps scrolle. Sans ça, un contenu
          // plus haut que l'écran débordait en haut ET en bas (titre coupé, aucun
          // moyen de fermer). `100dvh` = hauteur réelle sur mobile (barre navigateur).
          "flex max-h-[calc(100dvh-2rem)] w-full flex-col rounded-xl bg-white shadow-xl focus:outline-none",
          sizeClasses[size],
        ].join(" ")}
      >
        {/* En-tête fixe : titre + bouton fermer toujours visible (même corps scrollé). */}
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-champagne-100 px-6 py-4">
          {title ? (
            <h2 className="text-[18px] font-semibold text-aubergine-900">{title}</h2>
          ) : (
            <span aria-hidden="true" />
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="-mr-2 -mt-1 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-champagne-500 transition hover:bg-champagne-50 hover:text-champagne-800"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>
        {/* Corps scrollable — `overscroll-contain` évite que le scroll « fuie »
            sur la page derrière une fois arrivé en bout de course. */}
        <div className="overflow-y-auto overscroll-contain px-6 py-5">{children}</div>
      </div>
    </div>
  );
}
